import { useLocation, useNavigate, useParams } from "react-router-dom";
import React, { useMemo } from "react";
import { ERoutes } from "../AppRouter";

// Hook
export function useRouter() {
  const { id, tab, state } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const openNewTab = React.useCallback((route: string) => {
    let url = window.location.pathname.replace(window.location.hash, "");
    if (window.location.hash) {
      if (!url.startsWith("/")) {
        url += "/#";
      } else {
        url += "#";
      }
    }
    if (!route.startsWith("/")) {
      url += "/";
    }
    url += route;
    window.open(url);
  }, []);

  const editRouteParams = React.useMemo(() => {
    return {
      id,
      tab,
      editState: state,
    };
  }, [id, state, tab]);

  const goToEditPage = React.useCallback(
    (route: ERoutes, id: number, tab: string) => {
      navigate(`${route}/${id}/${tab}`);
    },
    [navigate]
  );

    const goToEditPageWithState = React.useCallback(
    (route: ERoutes, id: number, tab: string, state = "edit") => {
      navigate(`${route}/${id}/${tab}/${state}`);
    },
    [navigate]
  );

  const goToEditPageWithTreeView = React.useCallback(
    (route: ERoutes, id: number, tab: string, treeViewType: string) => {
      navigate(`${route}/${id}/${tab}/${treeViewType}`);
    },
    [navigate]
  );

  const getEditRoute = React.useCallback(
    (route: ERoutes, id: number, tab: string, queryParams: object = null) => {
      const queryString = queryParams
        ? "?" +
          Object.keys(queryParams)
            .map((k) => k + "=" + encodeURIComponent(queryParams[k]))
            .join("&")
        : "";
      return `${route}/${id}/${tab}/${queryString}`;
    },
    []
  );

  const currentBaseRoute = React.useMemo(
    () => "/" + location.pathname.split("/")[1],
    [location.pathname]
  );

  const goToEditPageOnOtherTab = React.useCallback(
    (route: ERoutes, id: number, tab: string) => {
      const win = window.open("#" + getEditRoute(route, id, tab), "_blank");
      win.focus();
    },
    [getEditRoute]
  );

  const goToPageOnOtherTab = React.useCallback((route: string) => {
    const win = window.open("#" + route, "_blank");
    win.focus();
  }, []);

  const addQueryObject = React.useCallback(
    (route: string, queryParams: object) => {
      const queryString =
        "?" +
        Object.keys(queryParams)
          .map((k) => k + "=" + encodeURIComponent(queryParams[k]))
          .join("&");
      return `${route}/${queryString}`;
    },
    []
  );

  const replaceEditRouteId = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (newId: number, paramName: string = "id") => {
      const newRoute = location.pathname.replace(id, newId.toString());
      navigate(newRoute, { replace: true });
    },
    [id, location.pathname, navigate]
  );

  // Return our custom router object
  // Memoize so that a new object is only returned if something changes
  return useMemo(() => {
    return {
      push: (to: string) => navigate(to),
      replace: (to: string) => navigate(to, { replace: true }),
      navigate,
      pathname: location.pathname,
      location,
      openNewTab,
      editRouteParams,
      getEditRoute,
      goToEditPage,
      goToEditPageOnOtherTab,
      goToPageOnOtherTab,
      addQueryObject,
      goToEditPageWithTreeView,
      currentBaseRoute,
      replaceEditRouteId,
    };
  }, [
    navigate,
    location,
    openNewTab,
    editRouteParams,
    getEditRoute,
    goToEditPage,
    goToEditPageOnOtherTab,
    goToPageOnOtherTab,
    addQueryObject,
    goToEditPageWithTreeView,
    currentBaseRoute,
    replaceEditRouteId,
  ]);
}
